import type { ReactNode } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import {
  MiFIDClassification,
  TsAlertProfileType,
  TsComparisonOperator,
  TsTradeSession,
} from '@/api/types/tsTemplates';
import type { TsAlertTemplateDto } from '@/api/types/tsTemplates';
import { formatDateTime as fmtDateTime } from '@/utils/format';
import { useTemplateDetail } from './api';

const TYPE_LABEL: Record<TsAlertProfileType, string> = {
  [TsAlertProfileType.AccountConditions]: 'Account Conditions',
  [TsAlertProfileType.ClosedTrades]: 'Closed Trades',
  [TsAlertProfileType.Deals]: 'Deals',
};

const SESSION_LABEL: Record<TsTradeSession, string> = {
  [TsTradeSession.NotApplicable]: 'N/A',
  [TsTradeSession.Sydney]: 'Sydney',
  [TsTradeSession.Tokyo]: 'Tokyo',
  [TsTradeSession.London]: 'London',
  [TsTradeSession.NewYork]: 'New York',
  [TsTradeSession.Custom]: 'Custom',
};

const OPERATOR_LABEL: Record<TsComparisonOperator, string> = {
  [TsComparisonOperator.NotApplicable]: '',
  [TsComparisonOperator.GreaterThan]: '>',
  [TsComparisonOperator.SmallerThan]: '<',
  [TsComparisonOperator.EqualTo]: '=',
};

const MIFID_LABEL: Record<MiFIDClassification, string> = {
  [MiFIDClassification.Retail]: 'Retail',
  [MiFIDClassification.PerSeProfessional]: 'Per Se Professional',
  [MiFIDClassification.ProfessionalOnRequest]: 'Professional On Request',
  [MiFIDClassification.EligibleCounterparty]: 'Eligible Counterparty',
  [MiFIDClassification.Default]: 'Default',
};

function Row({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <tr>
      <th className="text-nowrap" style={{ width: '45%' }}>
        {label}
      </th>
      <td>{value}</td>
    </tr>
  );
}

function withOperator(op: TsComparisonOperator | null | undefined, value: number | null | undefined) {
  if (value === null || value === undefined) return null;
  const opLabel = op != null ? OPERATOR_LABEL[op] : '';
  return opLabel ? `${opLabel} ${value}` : `${value}`;
}

function formatDateTime(value?: string | null) {
  if (!value) return null;
  return fmtDateTime(value);
}

function formatTime(value?: string | null) {
  if (!value) return null;
  return fmtDateTime(value);
}

function Details({ t }: { t: TsAlertTemplateDto }) {
  return (
    <table className="table table-sm table-striped mb-0">
      <tbody>
        <Row label="Template Name" value={t.profileTag} />
        <Row label="Type" value={t.type != null ? TYPE_LABEL[t.type] : t.typeName} />
        <Row label="Priority" value={t.priority} />
        <Row label="History Date" value={formatDateTime(t.historyDate)} />
        <Row label="Status" value={t.enabled ? 'Enabled' : 'Disabled'} />

        <Row
          label="Trading Session"
          value={t.tradeSession != null ? SESSION_LABEL[t.tradeSession] : null}
        />
        <Row label="Open Time" value={formatTime(t.openTime)} />
        <Row label="Close Time" value={formatTime(t.closeTime)} />
        <Row label="Trade Time (s)" value={t.tradeTime} />
        <Row label="Trade Points" value={t.tradePoints} />

        <Row label="Volume" value={withOperator(t.volumeOperator, t.volume)} />
        <Row label="Notional" value={withOperator(t.notionalOperator, t.notional)} />
        <Row label="Equity" value={withOperator(t.equityOperator, t.equity)} />
        <Row label="Margin Level %" value={withOperator(t.marginLevelOperator, t.marginLevel)} />

        {t.profitThresholdsPerTrade && (
          <Row label="Per-Trade P/L" value={t.profitThresholdsAmountPerTrade} />
        )}
        {t.cumulativeProfitThresholds && (
          <Row label="Cumulative P/L" value={t.cumulativeProfitThresholdsAmount} />
        )}
        {t.cumulativeProfitThresholds && (
          <Row
            label="Include Unrealized P/L"
            value={t.cumulativeProfitThresholdsFloating ? 'Yes' : 'No'}
          />
        )}

        <Row label="Overnight Days" value={t.overnightDays} />
        <Row
          label="MiFID Classification"
          value={t.mifidClassification != null ? MIFID_LABEL[t.mifidClassification] : null}
        />
        <Row label="Country" value={t.countryName} />
        {t.marInstruments && <Row label="Instruments" value="MAR Instruments" />}
        <Row label="Created" value={formatDateTime(t.creationTime)} />
        <Row label="Last Modified" value={formatDateTime(t.lastModificationTime)} />
      </tbody>
    </table>
  );
}

interface ViewTemplateModalProps {
  id: number | null;
  onClose: () => void;
}

export function ViewTemplateModal({ id, onClose }: ViewTemplateModalProps) {
  const query = useTemplateDetail(id);

  return (
    <Modal
      show={id !== null}
      title="View Template"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-light" onClick={onClose}>
          Close
        </button>
      }
    >
      {query.isLoading ? (
        <Spinner />
      ) : query.isError || !query.data ? (
        <ErrorState message="Could not load the template." onRetry={() => query.refetch()} />
      ) : (
        <Details t={query.data} />
      )}
    </Modal>
  );
}
